import { compareAlphabetically, isNil } from '@gfe/core/utils/shared-utils';

import { RegistrationRepairScope } from '@core/models/entities/entity-visualization-interface';
import {
  registrationSlotKey,
  registrationSlots
} from '@core/models/entities/implementations/user-entity-registrations';
import {
  UserRouteV1Model,
  computeRouteName,
  isRouteValidated
} from '@core/models/entities/implementations/user-route-v1';

import {
  REGISTRATION_KINDS,
  ResourceRegistrationsByKind,
  RoutePatch,
  UnrepairedSlot
} from './registration-repair.model';

/** The edited route first, then uid order, so the output is stable and a retry idempotent. */
function orderCandidates(routeUids: string[], priorityRouteUid: string): string[] {
  return [...routeUids].sort((a, b) => {
    if (a === priorityRouteUid) return -1;
    if (b === priorityRouteUid) return 1;
    return compareAlphabetically(a, b);
  });
}

/**
 * The fetched snapshot with the just-written fields applied to the edited route. The query runs
 * after the write, so a stale read would still show the route's pre-edit resources and slots —
 * hiding it from the unassigned set and making the slots it vacated look occupied.
 */
export function overlayWrittenRoute(
  routes: UserRouteV1Model[],
  fields: Record<string, unknown>
): UserRouteV1Model[] {
  const writtenUid = fields['uid'];
  return routes.map((route) =>
    route.uid === writtenUid ? ({ ...route, ...fields } as UserRouteV1Model) : route
  );
}

/**
 * Assigns each unassigned registration the first free slot on its resource.
 *
 * `routes` must be exhaustive for every resource named in `scope`: the free set is computed by
 * subtracting the slots sibling routes hold, so a missing sibling means handing out a slot that
 * is already taken. The caller guarantees this, which is why a route is only ever repaired for a
 * kind whose resource `scope` names.
 */
export function planRegistrationRepair(
  routes: UserRouteV1Model[],
  resources: ResourceRegistrationsByKind,
  scope: RegistrationRepairScope
): { patches: RoutePatch[]; unrepaired: UnrepairedSlot[] } {
  const routeByUid = new Map(routes.map((route) => [route.uid, route]));
  const patchByRoute = new Map<string, RoutePatch>();
  const unrepaired: UnrepairedSlot[] = [];

  for (const descriptor of REGISTRATION_KINDS) {
    const inScope = new Set(scope[descriptor.scopeField]);
    const resolved = resources[descriptor.kind];

    const held = new Map<string, Set<string>>();
    const candidates = new Map<string, string[]>();

    for (const route of routes) {
      const resourceUid = route[descriptor.resourceField];
      if (isNil(resourceUid) || !inScope.has(resourceUid)) continue;

      const registration = route[descriptor.registrationField];
      if (isNil(registration)) {
        // A validated route is frozen: any write to it is refused, so it can never be repaired.
        if (isRouteValidated(route)) continue;
        candidates.set(resourceUid, [...(candidates.get(resourceUid) ?? []), route.uid]);
        continue;
      }

      const taken = held.get(resourceUid) ?? new Set<string>();
      taken.add(registrationSlotKey(registration));
      held.set(resourceUid, taken);
    }

    for (const [resourceUid, routeUids] of candidates) {
      const ordered = orderCandidates(routeUids, scope.routeUid);

      if (!resolved.has(resourceUid)) {
        ordered.forEach((routeUid) =>
          unrepaired.push({
            routeUid,
            kind: descriptor.kind,
            resourceUid,
            totalSlots: 0
          })
        );
        continue;
      }

      const slots = registrationSlots(resolved.get(resourceUid));
      // No slots at all means registrations are not in use for this resource, so an unassigned
      // route is already correct and there is nothing to repair or to report.
      if (slots.length === 0) continue;

      const taken = held.get(resourceUid) ?? new Set<string>();
      const free = slots.filter((slot) => !taken.has(registrationSlotKey(slot)));

      ordered.forEach((routeUid, index) => {
        const slot = free[index];
        const route = routeByUid.get(routeUid);
        if (isNil(slot)) {
          unrepaired.push({
            routeUid,
            kind: descriptor.kind,
            resourceUid,
            totalSlots: slots.length
          });
          return;
        }
        if (isNil(route)) return;

        const patch = patchByRoute.get(routeUid) ?? { uid: routeUid };
        patch[descriptor.registrationField] = slot;
        if (descriptor.affectsName) {
          patch.name = computeRouteName(route, { registration: slot });
        }
        patchByRoute.set(routeUid, patch);
      });
    }
  }

  return { patches: [...patchByRoute.values()], unrepaired };
}
